import type { Props as MutualFollowConfirmDialogProps } from "Components/Social/MutualFollowConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useCallback, useState } from "react";

/** Who the caller is about to become mutual followers with — what the confirm names. */
export type MutualFollowCandidate = {
  handle?: string | null;
  /** The counterpart's tier: a Lite persona is disclosed nothing, so the name sentence is dropped. */
  isFullMoone?: boolean;
};

type PendingConfirm = {
  candidate: MutualFollowCandidate;
  confirmButtonText: string;
  onConfirmed: () => void;
};

/**
 * The confirm that guards every action completing a mutual follow — following back a public
 * profile, approving a request from someone the caller already follows (decision 2026-10-20).
 * Owns the dialog's open state and the pending action, so a host only decides WHEN to confirm:
 *
 *   const { confirmMutual, mutualConfirmDialogProps } = useMutualFollowConfirm();
 *   confirmMutual(candidate, t(RESOURCES.FollowBack), () => follow(id));
 *   <MutualFollowConfirmDialog {...mutualConfirmDialogProps} />
 *
 * One dialog per host, whichever row or button asked; the candidate travels with the request, so
 * a list host needs no "which row" state of its own.
 */
export const useMutualFollowConfirm = () => {
  const { isOpen, open, handleResult } = useConfirmDialog();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirmMutual = useCallback(
    (
      candidate: MutualFollowCandidate,
      confirmButtonText: string,
      onConfirmed: () => void,
    ) => {
      setPending({ candidate, confirmButtonText, onConfirmed });
      open();
    },
    [open],
  );

  // Closed means nothing pending: the answered request is released rather than held until the
  // next one overwrites it.
  const onResult = handleResult(() => pending?.onConfirmed());
  const mutualConfirmDialogProps: MutualFollowConfirmDialogProps = {
    isOpen,
    handle: pending?.candidate.handle,
    isCounterpartFullMoone: pending?.candidate.isFullMoone,
    confirmButtonText: pending?.confirmButtonText ?? "",
    handleResult: (wasConfirmed) => {
      onResult(wasConfirmed);
      setPending(null);
    },
  };

  return { confirmMutual, mutualConfirmDialogProps };
};
